import React, { useCallback, useMemo } from 'react';
import { Temporal } from '@js-temporal/polyfill';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';

import { useAppSelector } from '@/store/hooks';
import {
  selectIsSavingWorkingProject,
  selectProjectName,
  selectWorkingProjectDescription,
} from '@/store/slices/project/selectors';

import { Session } from '@/types/session.types';
import { ICalendarDetailMetadata } from '../../settings.types';

import TextArea from '@ui/text-area/text-area';
import useLanguageParams from '@/utils/use-language-params';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

interface ICalendarDetailProps {
  metadata: ICalendarDetailMetadata;
  setMetadata: (data: ICalendarDetailMetadata) => void;
  session: Session;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
}

const useCalendarDate = (session: Session) => {
  return useMemo(() => {
    if (session.timestamp) {
      const timeZone = Temporal.Now.timeZoneId();
      const date = Temporal.Instant.fromEpochMilliseconds(session.timestamp).toZonedDateTimeISO(timeZone);
      const endDate = Temporal.Instant.fromEpochMilliseconds(
        session.end_timestamp || session.timestamp
      ).toZonedDateTimeISO(timeZone);

      // Duration replaces the manual ms math
      const duration = date.until(endDate, { largestUnit: 'hours' });
      const { hours, minutes } = duration;
      const runtime = hours > 0 && minutes > 0 ? `${hours}h ${minutes}m` : hours > 0 ? `${hours}h` : `${minutes}m`;

      return {
        hasTimestamp: true,
        monthName: date.toLocaleString(navigator.language, { month: 'short' }),
        dayOfMonth: date.day,
        dayString: date.toLocaleString(navigator.language, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }),
        startTime: date.toLocaleString(navigator.language, { hour: '2-digit', minute: 'numeric' }),
        endTime: endDate
          .toLocaleString(navigator.language, {
            hour: '2-digit',
            minute: 'numeric',
            timeZoneName: 'longOffset',
          })
          .replace('GMT', ' • GMT'),
        runtime,
      };
    }
    return { hasTimestamp: false };
  }, [session.timestamp, session.end_timestamp]);
};

const CalendarDetail = ({ session, setMetadata, metadata, onSave, onCancel, saveDisabled }: ICalendarDetailProps) => {
  const projectDescription = useAppSelector(selectWorkingProjectDescription);
  const projectName = useAppSelector(selectProjectName);
  const isSaving = useAppSelector(selectIsSavingWorkingProject);

  const { monthName, dayOfMonth, dayString, startTime, endTime, runtime, hasTimestamp } = useCalendarDate(session);
  const language = useLanguageParams();

  const { calendarTitle, calendarDescription } = useMemo(() => {
    const resolvedTitle =
      metadata.name ?? session.calendar_settings?.content?.[language ?? 'base']?.title ?? projectName;
    const resolvedDescription =
      metadata.description ??
      session.calendar_settings?.content?.[language ?? 'base']?.description ??
      projectDescription;

    return {
      calendarTitle: resolvedTitle,
      calendarDescription: resolvedDescription,
    };
  }, [metadata, language, session, projectName, projectDescription]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMetadata({ ...metadata, name: e.target.value });
    },
    [metadata, setMetadata]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMetadata({ ...metadata, description: e.target.value });
    },
    [metadata, setMetadata]
  );

  if (!hasTimestamp) return null; // event has no scheduled time, can't show any calendar things

  return (
    <Container padding="16px 0 0 0" data-component="CalendarDetail">
      <Container className={'flex padding-20 gap-20 height-100-percent'} borderRadius="12px" design="GlobalCont2">
        <div className="flex-column flex-1">
          <div className="flex ai-center jc-space-between margin-b-24">
            <InputWithOverlay
              wrapperClass="flex-1"
              isBorderless
              typography="H5"
              designSize="small"
              design={null}
              inputHeight="20px"
              inputProps={{
                tabIndex: 0,
                value: calendarTitle,
                className: `padding-0 radius-4`,
                onChange: handleNameChange,
              }}
            />
          </div>
          <Container
            className="flex"
            gap="12px"
            padding="12px"
            borderRadius="12px"
            margin="0 0 8px 0"
            design="GlobalCont3"
          >
            <Container
              width="46px"
              borderRadius="8px"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="var(--global-cont-4)"
            >
              <Container
                style={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: '1px solid var(--global-cont-4)',
                }}
                background={'var(--global-opacity-white-10)'}
                height="20px"
                className="flex ai-center jc-center"
              >
                <Text className="C3 text-uppercase" design="GlobalIcoText2">
                  {monthName}
                </Text>
              </Container>
              <Container className="flex ai-center jc-center" height="26px">
                <Text className="H5" design="GlobalIcoText1">
                  {dayOfMonth}
                </Text>
              </Container>
            </Container>
            <Container className="flex flex-1" gap="12px">
              <Container className="flex flex-column jc-center as-stretch flex-1" gap="4px">
                <Text className="S3">{dayString}</Text>
                <Text className="C1" design="GlobalIcoText3">
                  {startTime} - {endTime} • Runtime: {runtime}
                </Text>
              </Container>

              <Container className="flex flex-column jc-center as-stretch flex-1" gap="4px">
                <Text className="S3">Duration</Text>
                <Text className="C1" design="GlobalIcoText3">
                  {runtime}
                </Text>
              </Container>
            </Container>
          </Container>
          <TextArea
            label="Description"
            placeholder="Enter your project description"
            value={calendarDescription}
            onChange={handleDescriptionChange}
            disabled={isSaving}
            minRows={3}
          />

          <div className="flex ai-center gap-8 flex-1 jc-flex-end margin-t-32">
            <Button
              backgroundColor="var(--gray-700)"
              hoverBackgroundColor="var(--gray-600)"
              height="32px"
              className="L2 padding-v-8 padding-h-16"
              onClick={onCancel}
              disabled={isSaving || saveDisabled}
              disabledOpacity={0.6}
            >
              <Text className="L2">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              padding="8px 16px"
              width="auto"
              onClick={onSave}
              disabled={isSaving || saveDisabled}
              disabledOpacity={0.6}
              hoverOpacity={0.8}
            >
              <Text color={getThemeColorContrastCSSVariable()} className="L2">
                Save
              </Text>
            </Button>
          </div>
        </div>
      </Container>
    </Container>
  );
};

export default CalendarDetail;
